import React, { useState, useRef, useEffect } from 'react';

/**
 * Smooth Image Carousel with swipe support
 * Features:
 * - Touch/mouse swipe navigation
 * - Smooth transitions
 * - Dot indicators
 * - Arrow navigation (desktop)
 * - Auto-play thumbnails to keep active in view
 */
export default function ImageCarousel({ 
  images = [], 
  currentIndex = 0, 
  onIndexChange,
  showArrows = true,
  showDots = true,
  className = ''
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [imageError, setImageError] = useState({});
  const containerRef = useRef(null);

  // Reset drag state when index changes externally
  useEffect(() => {
    setDragOffset(0);
    setIsDragging(false);
  }, [currentIndex]);

  const goToNext = () => {
    if (currentIndex < images.length - 1) {
      onIndexChange?.(currentIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      onIndexChange?.(currentIndex - 1);
    }
  };

  const handleDragStart = (clientX) => {
    setIsDragging(true);
    setDragStart(clientX);
    setDragOffset(0);
  };

  const handleDragMove = (clientX) => {
    if (!isDragging) return;
    const offset = clientX - dragStart;
    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    const threshold = 50; // Minimum swipe distance
    
    if (dragOffset < -threshold && currentIndex < images.length - 1) {
      // Swiped left - go to next
      onIndexChange?.(currentIndex + 1);
    } else if (dragOffset > threshold && currentIndex > 0) {
      // Swiped right - go to previous
      onIndexChange?.(currentIndex - 1);
    }
    
    setIsDragging(false);
    setDragOffset(0);
    setDragStart(0);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleDragMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Add/remove event listeners for mouse drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  if (!images || images.length === 0) {
    return (
      <div className={`image-carousel ${className}`}>
        <div className="carousel-placeholder">
          <div className="placeholder-icon">📦</div>
        </div>
      </div>
    );
  }

  const translateX = (-currentIndex * 100) + (isDragging ? (dragOffset / (containerRef.current?.offsetWidth || 1)) * 100 : 0);

  return (
    <div className={`image-carousel ${className}`} ref={containerRef}>
      {/* Main Image Container */}
      <div className="carousel-viewport">
        <div 
          className="carousel-track"
          style={{
            transform: `translateX(${translateX}%)`,
            transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: isDragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {images.map((img, idx) => (
            <div key={idx} className="carousel-slide">
              {imageError[idx] ? (
                <div className="carousel-placeholder">
                  <div className="placeholder-icon">📦</div>
                </div>
              ) : img.url ? (
                <img
                  src={img.url}
                  alt={img.alt_text || `Image ${idx + 1}`}
                  draggable={false}
                  onError={() => setImageError(prev => ({ ...prev, [idx]: true }))}
                />
              ) : (
                <div className="carousel-placeholder">
                  <div className="placeholder-icon">📦</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Arrow Navigation (Desktop) */}
        {showArrows && images.length > 1 && (
          <>
            <button
              className="carousel-arrow carousel-arrow-prev"
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              className="carousel-arrow carousel-arrow-next"
              onClick={goToNext}
              disabled={currentIndex === images.length - 1}
              aria-label="Next image"
            >
              ›
            </button>
          </>
        )}
      </div>

      {/* Dot Indicators */}
      {showDots && images.length > 1 && (
        <div className="carousel-dots">
          {images.map((_, idx) => (
            <button
              key={idx}
              className={`carousel-dot ${idx === currentIndex ? 'active' : ''}`}
              onClick={() => onIndexChange?.(idx)}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
